import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "context/AuthProvider";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import usePDFRenderer from "hooks/usePDFRenderer";
import Card from "components/ui-elements/Card";
import MyForm from "components/form-elements/MyForm";
import PopupDialog from "components/dialog/PopupDialog";
import PhotoDropUpload from "components/form-elements/PhotoDropUpload";
import Row from "components/ui-elements/Row";
import Col from "components/ui-elements/Col";
import { MyConst } from "data/MyConst";
import { RuleRequired } from "utils/MyUtils";
import { parseBoolean, showEmpty } from "features/kms/system/SystemPageUtils";
import { loadPhotoDataURL, SDMProfileDetail, SDMProfilePDF } from "features/kms/sdm/SDMKeolahragaanPage";

function InfoBox({ label, value, icon, color = "primary" }) {
  return (
    <div className={`small-box bg-${color}`}>
      <div className="inner">
        <div className="text-sm text-uppercase">{label}</div>
        <h5 className="mb-0">{value}</h5>
      </div>
      <div className="icon">
        <i className={`fas ${icon}`} />
      </div>
    </div>
  );
}

const emptyCompletionForm = {
  no_kk: "",
  nik: "",
  gelar_depan: "",
  nama_lengkap: "",
  gelar_belakang: "",
  tempat_lahir: "",
  tanggal_lahir: "",
  nomor_akta_lahir: "",
  tahun_akta_lahir: "",
  id_jenis_kelamin: "",
  golongan_darah: "",
  id_agama: "",
  kewarganegaraan: "",
  id_status_perkawinan: "",
  hp: "",
  telepon: "",
  email: "",
  alamat: "",
  kota: "",
  kode_pos: "",
  pekerjaan: "",
  jabatan: "",
  bidang_usaha: "",
  nama_tempat_kerja: "",
  telepon_tempat_kerja: "",
  alamat_tempat_kerja: "",
  pendapatan: "",
  sumber_pendapatan: "",
  npwp: "",
  nama_ayah: "",
  pekerjaan_ayah: "",
  nama_ibu: "",
  pekerjaan_ibu: "",
  alamat_orang_tua: "",
  nomor_rekening: "",
  rekening_atas_nama: "",
  bank: "",
  nomor_bpjs_kesehatan: "",
  status_bpjs_kesehatan: "",
  nomor_bpjs_ketenagakerjaan: "",
  status_bpjs_ketenagakerjaan: "",
  asuransi_lainnya: "",
  nomor_asuransi_lainnya: "",
};

function requiredLabel(label) {
  return (
    <>
      {label}
      <span className="text-danger ml-1">*</span>
    </>
  );
}

function pekerjaanAddConfig(title = "Tambah Pekerjaan") {
  return {
    title,
    url: "/references/pekerjaan",
    cellid: "id_pekerjaan",
    columns: ["nama"],
    coltype: {
      nama: {
        type: "text",
        label: "Nama Pekerjaan",
        name: "nama",
        validation: RuleRequired,
        colsize: 12,
      },
    },
    optionsUrl: "/references/pekerjaan",
    optionsAuth: true,
    otherLabel: "Lainnya",
  };
}

function PersonalCompletionForm({ item, onSaved }) {
  const [, , , axiosSave] = useAxiosFunction();
  const [jenisKelaminResp] = useAxios({ method: "GET", url: "/references/jenis-kelamin", auth: true });
  const [agamaResp] = useAxios({ method: "GET", url: "/references/agama", auth: true });
  const [statusPerkawinanResp] = useAxios({ method: "GET", url: "/references/status-perkawinan", auth: true });
  const [pekerjaanResp] = useAxios({ method: "GET", url: "/references/pekerjaan", auth: true });
  const [sumberPendapatanResp] = useAxios({
    method: "GET",
    url: "/references/sumber-pendapatan",
    auth: true,
  });

  const defaultValue = useMemo(() => {
    if (!item) return emptyCompletionForm;
    return {
      ...emptyCompletionForm,
      ...Object.keys(emptyCompletionForm).reduce((result, key) => {
        result[key] = item?.[key] ?? "";
        return result;
      }, {}),
      nik: item?.nik || "",
    };
  }, [item]);

  const jenisKelaminOptions = Array.isArray(jenisKelaminResp?.data) ? jenisKelaminResp.data : [];
  const agamaOptions = Array.isArray(agamaResp?.data) ? agamaResp.data : [];
  const statusPerkawinanOptions = Array.isArray(statusPerkawinanResp?.data)
    ? statusPerkawinanResp.data
    : [];
  const pekerjaanOptions = Array.isArray(pekerjaanResp?.data) ? pekerjaanResp.data : [];
  const sumberPendapatanOptions = Array.isArray(sumberPendapatanResp?.data)
    ? sumberPendapatanResp.data
    : [];
  const golonganDarahOptions = [
    { id: "A", nama: "A" },
    { id: "B", nama: "B" },
    { id: "AB", nama: "AB" },
    { id: "O", nama: "O" },
  ];
  const statusBPJSOptions = [
    { id: "AKTIF", nama: "Aktif" },
    { id: "NONAKTIF", nama: "Nonaktif" },
  ];

  const coltype = {
    no_kk: { label: requiredLabel("No. KK"), type: "text", name: "no_kk", colsize: 3, validation: RuleRequired, placeholder: "No. KK" },
    nik: { label: requiredLabel("NIK"), type: "text", name: "nik", colsize: 3, validation: RuleRequired, disabled: true, placeholder: "NIK" },
    gelar_depan: { label: "Gelar Depan", type: "text", name: "gelar_depan", colsize: 3 },
    nama_lengkap: { label: requiredLabel("Nama Lengkap"), type: "text", name: "nama_lengkap", colsize: 3, validation: RuleRequired, placeholder: "Nama Lengkap" },
    gelar_belakang: { label: "Gelar Belakang", type: "text", name: "gelar_belakang", colsize: 3 },
    tempat_lahir: { label: requiredLabel("Tempat Lahir"), type: "text", name: "tempat_lahir", colsize: 3, validation: RuleRequired, placeholder: "Tempat Lahir" },
    tanggal_lahir: { label: requiredLabel("Tanggal Lahir"), type: "date", name: "tanggal_lahir", colsize: 3, validation: RuleRequired, placeholder: "Tanggal Lahir" },
    nomor_akta_lahir: { label: "Nomor Akta Lahir", type: "text", name: "nomor_akta_lahir", colsize: 3 },
    tahun_akta_lahir: { label: "Tahun Akta Lahir", type: "number", name: "tahun_akta_lahir", colsize: 3 },
    id_jenis_kelamin: {
      label: requiredLabel("Jenis Kelamin"),
      type: "dropdown",
      name: "id_jenis_kelamin",
      colsize: 3,
      options: jenisKelaminOptions,
      valueId: "id_jenis_kelamin",
      labelId: "nama",
      placeholder: "-Pilih Jenis Kelamin-",
      validation: RuleRequired,
    },
    golongan_darah: {
      label: requiredLabel("Golongan Darah"),
      type: "dropdown",
      name: "golongan_darah",
      colsize: 3,
      options: golonganDarahOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Golongan Darah-",
      validation: RuleRequired,
    },
    id_agama: {
      label: requiredLabel("Agama"),
      type: "dropdown",
      name: "id_agama",
      colsize: 3,
      options: agamaOptions,
      valueId: "id_agama",
      labelId: "nama",
      placeholder: "-Pilih Agama-",
      validation: RuleRequired,
    },
    kewarganegaraan: { label: requiredLabel("Kewarganegaraan"), type: "text", name: "kewarganegaraan", colsize: 3, validation: RuleRequired, placeholder: "Kewarganegaraan" },
    id_status_perkawinan: {
      label: requiredLabel("Status Perkawinan"),
      type: "dropdown",
      name: "id_status_perkawinan",
      colsize: 3,
      options: statusPerkawinanOptions,
      valueId: "id_status_perkawinan",
      labelId: "nama",
      placeholder: "-Pilih Status Perkawinan-",
      validation: RuleRequired,
    },
    hp: { label: requiredLabel("HP"), type: "text", name: "hp", colsize: 3, validation: RuleRequired, placeholder: "HP" },
    telepon: { label: "Telepon", type: "text", name: "telepon", colsize: 3 },
    email: { label: requiredLabel("Email"), type: "email", name: "email", colsize: 3, validation: RuleRequired, placeholder: "Email" },
    alamat: { label: requiredLabel("Alamat"), type: "textarea", name: "alamat", colsize: 9, validation: RuleRequired, placeholder: "Alamat" },
    kota: { label: requiredLabel("Kab/Kota"), type: "text", name: "kota", colsize: 3, validation: RuleRequired, placeholder: "Kab/Kota" },
    kode_pos: { label: requiredLabel("Kode POS"), type: "text", name: "kode_pos", colsize: 3, validation: RuleRequired, placeholder: "Kode POS" },
    div_pekerjaan: { type: "divider", label: "Data Pekerjaan" },
    pekerjaan: {
      label: "Pekerjaan",
      type: "dropdowndinamis",
      name: "pekerjaan",
      colsize: 3,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan-",
      add_config: pekerjaanAddConfig("Tambah Pekerjaan"),
    },
    jabatan: { label: "Jabatan di Tempat Kerja", type: "text", name: "jabatan", colsize: 3 },
    bidang_usaha: { label: "Bidang Usaha Tempat Kerja", type: "text", name: "bidang_usaha", colsize: 3 },
    nama_tempat_kerja: { label: "Nama Tempat Kerja", type: "text", name: "nama_tempat_kerja", colsize: 3 },
    telepon_tempat_kerja: { label: "Telepon Tempat Kerja", type: "text", name: "telepon_tempat_kerja", colsize: 3 },
    alamat_tempat_kerja: { label: "Alamat Tempat Kerja", type: "textarea", name: "alamat_tempat_kerja", colsize: 6 },
    pendapatan: { label: "Pendapatan", type: "number", name: "pendapatan", colsize: 3 },
    sumber_pendapatan: {
      label: "Sumber Pendapatan",
      type: "dropdowndinamis",
      name: "sumber_pendapatan",
      colsize: 3,
      options: sumberPendapatanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Sumber Pendapatan-",
      add_config: {
        title: "Tambah Sumber Pendapatan",
        url: "/references/sumber-pendapatan",
        cellid: "id_sumber_pendapatan",
        columns: ["nama"],
        coltype: {
          nama: {
            type: "text",
            label: "Nama Sumber Pendapatan",
            name: "nama",
            validation: RuleRequired,
            colsize: 12,
          },
        },
        optionsUrl: "/references/sumber-pendapatan",
        optionsAuth: true,
        otherLabel: "Lainnya",
      },
    },
    npwp: { label: "NPWP", type: "text", name: "npwp", colsize: 3 },
    div_orang_tua: { type: "divider", label: "Data Orang Tua" },
    nama_ayah: { label: requiredLabel("Nama Ayah"), type: "text", name: "nama_ayah", colsize: 3, validation: RuleRequired, placeholder: "Nama Ayah" },
    pekerjaan_ayah: {
      label: requiredLabel("Pekerjaan Ayah"),
      type: "dropdowndinamis",
      name: "pekerjaan_ayah",
      colsize: 3,
      validation: RuleRequired,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan Ayah-",
      add_config: pekerjaanAddConfig("Tambah Pekerjaan Ayah"),
    },
    nama_ibu: { label: requiredLabel("Nama Ibu"), type: "text", name: "nama_ibu", colsize: 3, validation: RuleRequired, placeholder: "Nama Ibu" },
    pekerjaan_ibu: {
      label: requiredLabel("Pekerjaan Ibu"),
      type: "dropdowndinamis",
      name: "pekerjaan_ibu",
      colsize: 3,
      validation: RuleRequired,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan Ibu-",
      add_config: pekerjaanAddConfig("Tambah Pekerjaan Ibu"),
    },
    alamat_orang_tua: { label: requiredLabel("Alamat Orang Tua"), type: "textarea", name: "alamat_orang_tua", colsize: 12, validation: RuleRequired, placeholder: "Alamat Orang Tua" },
    div_rekening: { type: "divider", label: "Rekening Bank" },
    nomor_rekening: { label: "Nomor Rekening", type: "text", name: "nomor_rekening", colsize: 4 },
    rekening_atas_nama: { label: "Rekening Atas Nama", type: "text", name: "rekening_atas_nama", colsize: 4 },
    bank: { label: "Bank", type: "text", name: "bank", colsize: 4 },
    div_bpjs: { type: "divider", label: "BPJS dan Asuransi" },
    nomor_bpjs_kesehatan: { label: "No. BPJS Kesehatan", type: "text", name: "nomor_bpjs_kesehatan", colsize: 3 },
    status_bpjs_kesehatan: {
      label: "Status BPJS Kesehatan",
      type: "dropdown",
      name: "status_bpjs_kesehatan",
      colsize: 3,
      options: statusBPJSOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Status-",
    },
    nomor_bpjs_ketenagakerjaan: { label: "No. BPJS Ketenagakerjaan", type: "text", name: "nomor_bpjs_ketenagakerjaan", colsize: 3 },
    status_bpjs_ketenagakerjaan: {
      label: "Status BPJS Ketenagakerjaan",
      type: "dropdown",
      name: "status_bpjs_ketenagakerjaan",
      colsize: 3,
      options: statusBPJSOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Status-",
    },
    asuransi_lainnya: { label: "Asuransi Lainnya", type: "text", name: "asuransi_lainnya", colsize: 6 },
    nomor_asuransi_lainnya: { label: "Nomor Asuransi Lainnya", type: "text", name: "nomor_asuransi_lainnya", colsize: 6 },
  };

  const columns = [
    "no_kk",
    "nik",
    "nama_lengkap",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "div_pekerjaan",
    "pekerjaan",
    "jabatan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "div_orang_tua",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "div_rekening",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "div_bpjs",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
  ];

  const save = async (reqdata) => {
    const resp = await axiosSave({
      method: "PUT",
      url: "/profile/personal",
      auth: true,
      data: {
        ...reqdata,
        id_jenis_kelamin: Number(reqdata.id_jenis_kelamin || 0),
        id_agama: Number(reqdata.id_agama || 0),
        id_status_perkawinan: Number(reqdata.id_status_perkawinan || 0),
      },
    });
    if (resp?.data?.success === true) {
      onSaved?.();
    }
    return resp;
  };

  return (
    <>
      <div className="alert alert-info">
        Profil personal Anda belum lengkap. Silakan lengkapi data wajib terlebih dahulu agar dapat menggunakan menu SIMAKOR.
        <div className="small mt-1">
          Kolom bertanda <span className="text-danger">*</span> wajib diisi.
        </div>
      </div>
      <MyForm
        title="Lengkapi Profil"
        columns={columns}
        coltype={coltype}
        defaultValue={defaultValue}
        saveHandle={save}
        onSuccess={() => {}}
        onCancel={() => {}}
        lblsave="Simpan Profil"
        lblcancel="Batal"
      />
    </>
  );
}

export default function MyProfilePage() {
  const { user, replaceUser } = useAuth();
  const [showMsg] = useMsgBox();
  const [, , savingPhoto, axiosSavePhoto] = useAxiosFunction();
  const [, , loadingProfilePDF, axiosFetchProfilePDF] = useAxiosFunction();
  const [getProfilePDFViewer, toggleProfilePDFViewer] = usePDFRenderer();
  const [photoPopup, setPhotoPopup] = useState(false);
  const [draftPhoto, setDraftPhoto] = useState("");
  const [profilePDFData, setProfilePDFData] = useState(null);
  const isPersonal = String(user?.groupname || "").toUpperCase() === "PERSONAL";
  const [bootstrapResp, bootstrapError, bootstrapLoading] = useAxios({
    method: "GET",
    url: "/app/bootstrap",
    auth: true,
  });
  const [personalProfileResp, personalProfileError, personalProfileLoading, refetchPersonalProfile] = useAxios({
    method: "GET",
    url: isPersonal ? "/profile/personal" : "",
    auth: true,
  });

  const profile = bootstrapResp?.user || user || {};
  const contexts = Array.isArray(profile?.contexts) ? profile.contexts : [];
  const activeContext = profile?.active_context || null;

  const photoUrl = useMemo(() => {
    if (profile?.photo) {
      const location = profile?.photo_location || "profile";
      return `${String(MyConst.URLASET || "").replace(/\/$/, "")}/${location}/${profile.photo}`;
    }
    return `${process.env.PUBLIC_URL || ""}/assets/dist/img/avatar.png`;
  }, [profile?.photo, profile?.photo_location]);

  if (isPersonal) {
    const item = personalProfileResp?.data || null;
    const title = item?.peran_name || item?.peran_code || "SDM";
    const openPhotoPopup = () => {
      setDraftPhoto(item?.photo_url || "");
      setPhotoPopup(true);
    };
    const closePhotoPopup = () => setPhotoPopup(false);
    const profileCompleted = async () => {
      await replaceUser({ ...user, is_complete: true });
      if (typeof refetchPersonalProfile === "function") {
        refetchPersonalProfile();
      }
    };
    const showProfilePDF = async () => {
      const result = await axiosFetchProfilePDF({
        method: "GET",
        url: "/profile/personal",
        auth: true,
      });
      if (result?.data) {
        const photoDataURL = await loadPhotoDataURL(result.data?.photo_url, user?.token);
        setProfilePDFData({
          ...result.data,
          photo_data_url: photoDataURL,
        });
        toggleProfilePDFViewer();
      }
    };
    const savePhoto = async () => {
      if (!draftPhoto) {
        await showMsg("Informasi", "Silakan upload foto terlebih dahulu.");
        return;
      }
      const hasil = await axiosSavePhoto({
        method: "PUT",
        url: "/profile/personal/photo",
        auth: true,
        data: { photo_url: draftPhoto },
      });
      if (hasil?.data?.success === true) {
        await showMsg("Informasi", "Foto profil berhasil disimpan.");
        await replaceUser({ ...user, photo: draftPhoto, photo_location: "personal" });
        setPhotoPopup(false);
        if (typeof refetchPersonalProfile === "function") {
          refetchPersonalProfile();
        }
        return;
      }
      await showMsg("Error", hasil?.message || "Foto profil gagal disimpan.");
    };

    return (
      <>
        {personalProfileLoading || bootstrapLoading ? <div>Memuat profil...</div> : null}
        {personalProfileError ? (
          <div className="alert alert-danger mb-0">Profil SDM belum dapat dimuat.</div>
        ) : null}
        {!personalProfileLoading && !personalProfileError && item && user?.is_complete === false ? (
          <PersonalCompletionForm item={item} onSaved={profileCompleted} />
        ) : null}
        {!personalProfileLoading && !personalProfileError && item && user?.is_complete !== false ? (
          <SDMProfileDetail
            item={item}
            title={title}
            printId={`my-profile-${item?.id_keanggotaan || "personal"}`}
            educationUrl="/profile/pendidikan"
            achievementUrl="/profile/prestasi"
            certificationUrl="/profile/sertifikasi"
            achievementOptionsBaseUrl="/profile/capaian"
            photoAction={
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={openPhotoPopup}>
                <i className="fas fa-camera mr-1" />
                Edit Foto
              </button>
            }
          />
        ) : null}
        {!personalProfileLoading && !personalProfileError && item && user?.is_complete !== false ? (
          <Card className="mb-0">
            <div className="text-right">
              <button
                type="button"
                className="btn btn-danger"
                disabled={loadingProfilePDF}
                onClick={showProfilePDF}
              >
                <i className="fas fa-file-pdf mr-1" />
                {loadingProfilePDF ? "Memuat PDF..." : "PDF"}
              </button>
            </div>
          </Card>
        ) : null}
        <PopupDialog
          title="Edit Foto Profil"
          size="md"
          show={photoPopup}
          closeHandle={closePhotoPopup}
          doHandle={savePhoto}
          doLabel={savingPhoto ? "Menyimpan..." : "Simpan"}
          doDisabled={savingPhoto || !draftPhoto}
        >
          <PhotoDropUpload
            name="photo_url"
            dropname="photo"
            url="/file/upload"
            auth
            data={{ name: "photo", location: "personal" }}
            value={draftPhoto}
            setValue={(name, value) => setDraftPhoto(value)}
            onSuccess={(filename) => setDraftPhoto(filename)}
            previewWidth={180}
            previewHeight={180}
            caption="Upload foto baru, lalu klik Simpan."
          />
        </PopupDialog>
        {profilePDFData &&
          getProfilePDFViewer({
            title: `Profil ${title}`,
            orientation: "portrait",
            modalSize: "xl",
            modalZIndex: 2000,
            pageStyle: {
              paddingTop: 36,
              paddingHorizontal: 36,
              paddingBottom: 24,
            },
            content: <SDMProfilePDF item={profilePDFData} title={title} />,
          })}
      </>
    );
  }

  return (
    <>
      <Row>
        <Col size="3">
          <InfoBox label="Username" value={showEmpty(profile?.username)} icon="fa-user" color="info" />
        </Col>
        <Col size="3">
          <InfoBox label="Group Aktif" value={showEmpty(profile?.groupname)} icon="fa-users-cog" color="primary" />
        </Col>
        <Col size="3">
          <InfoBox label="Jumlah Peran" value={String(contexts.length || 0)} icon="fa-user-tag" color="success" />
        </Col>
        <Col size="3">
          <InfoBox label="Harus Ganti Password" value={parseBoolean(profile?.must_change_password) ? "YA" : "TIDAK"} icon="fa-key" color="warning" />
        </Col>
      </Row>

      <Row>
        <Col size="4">
          <Card title="Identitas Pengguna">
            <div className="text-center mb-3">
              <img
                src={photoUrl}
                alt="Profil"
                className="img-circle elevation-2"
                style={{ width: 96, height: 96, objectFit: "cover", objectPosition: "center" }}
              />
            </div>
            <table className="table table-sm table-borderless">
              <tbody>
                <tr><th style={{ width: 150 }}>Username</th><td>{showEmpty(profile?.username)}</td></tr>
                <tr><th>Email</th><td>{showEmpty(profile?.email)}</td></tr>
                <tr><th>Nama/Unit</th><td>{showEmpty(profile?.unit)}</td></tr>
                <tr><th>ID User</th><td className="text-monospace">{showEmpty(profile?.uid)}</td></tr>
                <tr><th>ID Profil</th><td className="text-monospace">{showEmpty(profile?.pid)}</td></tr>
                <tr><th>Last Login</th><td>{showEmpty(profile?.last_login_at)}</td></tr>
              </tbody>
            </table>
          </Card>
        </Col>

        <Col size="8">
          <Card title="Peran Aktif">
            <table className="table table-sm">
              <tbody>
                <tr><th style={{ width: 180 }}>Label Context</th><td>{showEmpty(activeContext?.label || profile?.active_context?.label)}</td></tr>
                <tr><th>Group</th><td>{showEmpty(profile?.groupname)}</td></tr>
                <tr><th>Context Type</th><td>{showEmpty(profile?.context_type)}</td></tr>
                <tr><th>Context ID</th><td className="text-monospace">{showEmpty(profile?.context_id)}</td></tr>
                <tr><th>ID User Group</th><td className="text-monospace">{showEmpty(profile?.id_user_group)}</td></tr>
                <tr><th>Versi App</th><td>{showEmpty(profile?.app_info?.version || profile?.version || MyConst.VERSION)}</td></tr>
              </tbody>
            </table>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col size="12">
          <Card title="Daftar Peran / Context Tersedia">
            {bootstrapLoading ? <div>Memuat daftar peran...</div> : null}
            {bootstrapError ? <div className="alert alert-danger mb-0">Gagal memuat profil: {bootstrapError}</div> : null}
            {!bootstrapLoading && !bootstrapError && contexts.length < 1 ? <div className="text-muted">User ini belum memiliki peran/context aktif.</div> : null}
            {!bootstrapLoading && !bootstrapError && contexts.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Aktif</th>
                      <th>Label</th>
                      <th>Group</th>
                      <th>Context Type</th>
                      <th>Context ID</th>
                      <th>Valid Until</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contexts.map((item) => {
                      const isActive = item?.id_user_group === profile?.id_user_group;
                      return (
                        <tr key={item?.id_user_group}>
                          <td className="text-center">{isActive ? <span className="badge badge-success">Aktif</span> : <span className="badge badge-light">-</span>}</td>
                          <td>{showEmpty(item?.label)}</td>
                          <td>{showEmpty(item?.groupname)}</td>
                          <td>{showEmpty(item?.context_type)}</td>
                          <td className="text-monospace">{showEmpty(item?.context_id)}</td>
                          <td>{showEmpty(item?.valid_until)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Card>
        </Col>
      </Row>
    </>
  );
}
